import {
  IndicatorConfigsFilter,
  Job,
  Manifest,
  NestedJobStop,
  NestedStopManifest,
  SearchIndicatorConfigsQuery,
  Stop,
  useSearchIndicatorConfigsQuery
} from "../generated/graphql";
import {createContext, useCallback, useContext, useEffect, useState} from "react";
import {ApolloError} from "@apollo/client";
import {RuleProperties, Operator, Engine, Rule} from "json-rules-engine";
import {addMinutes} from "date-fns";
import {IndicatorLogicSettings} from "../components/settings/ColorSettings/ColorizedIndicators/types/indicatorLogicSettings";
import {JobTypes} from "../components/settings/ColorSettings/ColorizedIndicators/types/jobTypeSelectorValues";
import {UserColor} from "../components/settings/ColorSettings/ColorizedIndicators/types/indicatorLogicType";
import _ from "lodash";
import {getArrayFromField} from "../utils/General";
import {Constants} from "../constants/constants";
import log from "loglevel";
import {TManifestStopEnhanced} from "../components/common/types";
import {useFeatureFlagContext} from "./FeatureFlagProvider";
import {ConditionNames} from "../components/settings/ColorSettings/ColorizedIndicators/types/conditionNames";

type DispatchRulesDataProviderProps = {
  children: JSX.Element[] | JSX.Element;
  filter?: IndicatorConfigsFilter;
  jobType: JobTypes;
};

type ManifestEngineProps = {
  manifest: Manifest | NestedStopManifest;
  stop?: TManifestStopEnhanced;
};

type JobEngineProps = {
  job: Job;
  stop: NestedJobStop;
};

type StopEngineProps = {
  stop: Stop;
};

type DispatchRulesDataState = {
  rules: RuleProperties[] | undefined;
  colorRank: ColorRank[] | undefined;
  loading: boolean;
  getConfiguredEngine(
    fact: ManifestEngineProps | JobEngineProps | StopEngineProps,
    rules: RuleProperties[]
  ): Engine | undefined;
  // DP-3292: manifest-only colour rules, evaluated against the manifest alone. Deliberately
  // separate from getConfiguredEngine rather than a flag on it — see the implementation.
  getManifestOnlyEngine(manifest: ManifestEngineProps["manifest"]): Engine | undefined;
};

type ColorRank = {
  color: UserColor | undefined;
  rank: number;
};

type EventOperatorProps = {
  lowerBound: number;
  upperBound: number;
  comparedFactorPaths?: string[];
};

const initialDispatchRulesDataState: DispatchRulesDataState = {
  rules: undefined,
  loading: true,
  colorRank: undefined,
  getConfiguredEngine: () => {
    log.debug("Engine not configured");
    return undefined;
  },
  getManifestOnlyEngine: () => {
    log.debug("Engine not configured");
    return undefined;
  }
};

enum CustomOperators {
  BeforeEvent = "beforeEvent",
  AfterEvent = "afterEvent",
  BeforeAfterEvent = "beforeAfterEvent"
}

const isValidDate = (date: any) => {
  return date !== null && date !== undefined && new Date(date).toString() !== "Invalid Date";
};

const validateFact = (fact: any) => {
  return fact !== null && fact !== undefined;
};

const createOperator = (
  operatorName: CustomOperators,
  calculateRange: (compareToDate: Date, jsonValue: EventOperatorProps) => [Date, Date]
) => {
  return new Operator(
    operatorName,
    (factValue: any, jsonValue: EventOperatorProps) => {
      let factDate, compareToDate;
      if (jsonValue.comparedFactorPaths?.length === 2) {
        if (
          !isValidDate(factValue[jsonValue.comparedFactorPaths[0]]) ||
          !isValidDate(factValue[jsonValue.comparedFactorPaths[1]])
        ) {
          return false;
        }
        factDate = new Date(factValue[jsonValue.comparedFactorPaths[0]]);
        compareToDate = new Date(factValue[jsonValue.comparedFactorPaths[1]]);
      } else {
        factDate = new Date();
        compareToDate = new Date(factValue);
      }

      const [rangeStart, rangeEnd] = calculateRange(compareToDate, jsonValue);

      const lowerResult = jsonValue.lowerBound === 999999 ? true : factDate >= rangeStart;
      const upperResult = jsonValue.upperBound === 999999 ? true : factDate <= rangeEnd;

      return lowerResult && upperResult;
    },
    (factValue: any) => {
      return validateFact(factValue);
    }
  );
};

const customOperators: Operator[] = [
  createOperator(CustomOperators.BeforeEvent, (compareToDate, jsonValue) => [
    addMinutes(compareToDate, jsonValue.lowerBound * -1),
    addMinutes(compareToDate, jsonValue.upperBound * -1)
  ]),
  createOperator(CustomOperators.AfterEvent, (compareToDate, jsonValue) => [
    addMinutes(compareToDate, jsonValue.lowerBound),
    addMinutes(compareToDate, jsonValue.upperBound)
  ]),
  createOperator(CustomOperators.BeforeAfterEvent, (compareToDate, jsonValue) => [
    addMinutes(compareToDate, jsonValue.lowerBound * -1),
    addMinutes(compareToDate, jsonValue.upperBound)
  ])
];

const DispatchRulesDataContext = createContext<DispatchRulesDataState>(initialDispatchRulesDataState);

const DispatchRulesDataProvider = ({children, jobType}: DispatchRulesDataProviderProps) => {
  const [rulesLoading, setRulesLoading] = useState(true);
  const [ruleData, setRuleData] = useState<RuleProperties[]>([]);
  const [colorRank, setColorRank] = useState<ColorRank[]>([]);
  const [state, setState] = useState<DispatchRulesDataState>({
    ...initialDispatchRulesDataState
  });
  const {backendColorRules} = useFeatureFlagContext();

  const {refetch} = useSearchIndicatorConfigsQuery({
    pollInterval: Constants.RULE_REFRESH_INTERVAL,
    variables: {
      filter: {
        siteId: {eq: 0}
      }
    },
    onCompleted(data) {
      onCompletedHandler(data);
    },
    onError(error) {
      onErrorHandler(error);
    }
  });

  const onCompletedHandler = useCallback(
    (data: SearchIndicatorConfigsQuery) => {
      if ((data?.searchIndicatorConfigs?.items ?? []).length > 0) {
        const ruleContainerObj: IndicatorLogicSettings = JSON.parse(data?.searchIndicatorConfigs?.items?.[0]?.config);

        const colorRank: ColorRank[] =
          ruleContainerObj?.rules
            ?.filter((rule) => rule.color)
            .map((rule) => {
              return {
                color: rule.color,
                rank: rule.rank
              };
            })
            .filter(
              (obj, index, self) =>
                index === self.findIndex((item) => item.color === obj.color && item.rank === obj.rank)
            ) ?? [];

        const sorted = getArrayFromField(ruleContainerObj?.rules);
        sorted.sort((a, b) => b.rank - a.rank); //inverting rank for engine
        let rank = 0;

        const rules: RuleProperties[] = sorted
          .filter((rule) => rule.rule && jobType === rule.type?.jobTypeValue)
          .map((rule) => {
            return {
              ...rule.rule,
              name: rule.key,
              priority: ++rank
            };
          });

        setColorRank(colorRank);
        setRuleData([...rules]);
      }
      setRulesLoading(false);
    },
    [jobType]
  );

  const onErrorHandler = useCallback(
    (error: ApolloError) => {
      log.error("[ERROR] - ManifestDataContext", error);
      // Wait for refresh token
      setTimeout(() => {
        refetch();
      }, 1000);
    },
    [refetch]
  );

  const getConfiguredEngine = useCallback(
    (fact: ManifestEngineProps | JobEngineProps, rules: RuleProperties[] | undefined) => {
      if (backendColorRules) return undefined;
      if (fact === undefined || rules?.length === 0) return undefined;

      const engine = new Engine();

      if (jobType === JobTypes.UnassignedJobs) {
        const sortedStops = _.sortBy((fact as JobEngineProps).job.stops, ["sequence"]);
        engine.addFact("job", {...(fact as JobEngineProps).job, stops: sortedStops});
      } else {
        engine.addFact("manifest", (fact as ManifestEngineProps).manifest);
      }

      engine.addFact("stop", fact.stop);
      customOperators.forEach((op) => engine.addOperator(op));

      rules?.forEach((rule) => {
        try {
          engine.addRule(new Rule({...rule}));
        } catch (e) {
          log.error("Error adding rule to engine", e);
        }
      });
      return engine;
    },
    [backendColorRules, jobType]
  );

  // DP-3292. A rule with a Manifest Attribute and no comparison type is tagged
  // `conditions.name = "ManifestOnly"` by RuleGenerator.service.ts. It is a *manifest* colour
  // rule: it colours the manifest header and never a stop, which is why the generator omits
  // the stop-type and stop-status leaves and why every stop-colouring call site filters these
  // rules out.
  //
  // getConfiguredEngine returns undefined for everything once `backendColorRules` is on,
  // because the worker owns stop colours from then on. But the worker cannot own the manifest
  // header colour: there is no manifest-level colour field anywhere — not on the `manifests`
  // index (indicator fields live only under `stops.*`), not on the GraphQL `Manifest` type —
  // so the header is always derived here, from `getHighestRankedColor`. With the flag on and
  // the rule correctly claiming no stop, nothing would be left to derive it from.
  //
  // Hence a second, deliberately narrow door rather than a condition on the existing one. It
  // takes only a manifest, selects only ManifestOnly rules itself, and adds no `stop` fact, so
  // no caller can reach through it to re-enable client-side STOP colouring under the flag and
  // start fighting the worker's stamps.
  const getManifestOnlyEngine = useCallback(
    (manifest: ManifestEngineProps["manifest"]) => {
      const manifestOnlyRules = ruleData?.filter((rule) => rule.conditions.name === ConditionNames.MANIFESTONLY) ?? [];
      if (!manifest || manifestOnlyRules.length === 0) return undefined;

      const engine = new Engine();
      engine.addFact("manifest", manifest);
      customOperators.forEach((op) => engine.addOperator(op));

      manifestOnlyRules.forEach((rule) => {
        try {
          engine.addRule(new Rule({...rule}));
        } catch (e) {
          log.error("Error adding manifest-only rule to engine", e);
        }
      });
      return engine;
    },
    [ruleData]
  );

  useEffect(() => {
    setState({
      ...initialDispatchRulesDataState,
      loading: rulesLoading,
      rules: ruleData,
      colorRank: colorRank,
      getConfiguredEngine,
      getManifestOnlyEngine
    });
  }, [colorRank, getConfiguredEngine, getManifestOnlyEngine, ruleData, rulesLoading]);

  return <DispatchRulesDataContext.Provider value={state}>{children}</DispatchRulesDataContext.Provider>;
};

const useDispatchRulesDataContext = () => {
  const context = useContext(DispatchRulesDataContext);
  if (context === undefined) {
    throw new Error("useDispatchRulesDataContext must be used within a DispatchRulesDataProvider");
  }
  return context;
};

export default DispatchRulesDataProvider;
export {DispatchRulesDataContext, CustomOperators, useDispatchRulesDataContext};
export type {DispatchRulesDataState, ColorRank, EventOperatorProps};
